/* localStorage-backed thread store for the LLM playground.
 *
 * Records are stored in the exact ExportedMessageRepository shape assistant-ui
 * uses ({ headId, messages: [{ parentId, message }] }) so a future server-side
 * graduation is a bulk import, not a rework. Keys are client-scoped: two
 * accounts on one browser must never see each other's threads.
 *
 * Persistence failure (quota, disabled storage) must never break the chat —
 * every write is guarded, worst case the conversation continues unpersisted.
 */

const PREFIX = 'uro.pg.v1';
const MAX_THREADS = 50;
const QUOTA_RETRIES = 5;

const indexKey = (clientId) => `${PREFIX}.${clientId}.index`;
const threadKey = (clientId, threadId) => `${PREFIX}.${clientId}.t.${threadId}`;

const listeners = new Set();
function emit() { listeners.forEach(l => { try { l(); } catch {} }); }
export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Cross-tab: another tab's writes fire the storage event — forward to listeners
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key && e.key.startsWith(PREFIX)) emit();
  });
}

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

/* Snapshot cache so useSyncExternalStore gets a stable reference while the
   underlying JSON string is unchanged. */
let snapshotCache = { clientId: null, raw: null, value: [] };
export function getThreadIndex(clientId) {
  let raw = null;
  try { raw = localStorage.getItem(indexKey(clientId)); } catch {}
  if (snapshotCache.clientId === clientId && snapshotCache.raw === raw) {
    return snapshotCache.value;
  }
  let list = [];
  try { list = raw ? JSON.parse(raw) : []; } catch { list = []; }
  list.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  snapshotCache = { clientId, raw, value: list };
  return list;
}

function evictOldest(clientId, protectId) {
  const list = readJSON(indexKey(clientId), [])
    .sort((a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''));
  const victim = list.find(t => t.id !== protectId);
  if (!victim) return false;
  try { localStorage.removeItem(threadKey(clientId, victim.id)); } catch {}
  try {
    localStorage.setItem(indexKey(clientId), JSON.stringify(list.filter(t => t.id !== victim.id)));
  } catch {}
  return true;
}

function trySetItem(key, value, clientId, protectId) {
  for (let i = 0; i <= QUOTA_RETRIES; i++) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      if (!evictOldest(clientId, protectId)) break;
    }
  }
  console.warn('playground threads: persist failed — continuing unpersisted');
  return false;
}

export function readThread(clientId, threadId) {
  return readJSON(threadKey(clientId, threadId), null);
}

export function deleteThread(clientId, threadId) {
  try { localStorage.removeItem(threadKey(clientId, threadId)); } catch {}
  // Index writes always read-modify-write from a fresh getItem (multi-tab:
  // last-writer-wins, never an in-memory copy).
  const list = readJSON(indexKey(clientId), []).filter(t => t.id !== threadId);
  try { localStorage.setItem(indexKey(clientId), JSON.stringify(list)); } catch {}
  emit();
}

function upsertThreadAndIndex(clientId, threadId, repo, meta) {
  const wrote = trySetItem(threadKey(clientId, threadId), JSON.stringify(repo), clientId, threadId);
  if (!wrote) return;

  const now = new Date().toISOString();
  const list = readJSON(indexKey(clientId), []);
  const existing = list.find(t => t.id === threadId);
  let next;
  if (existing) {
    existing.updated_at = now;
    existing.message_count = repo.messages.length;
    if (meta?.model) existing.model = meta.model;
    if (meta?.params) existing.params = meta.params;
    if (!existing.title && meta?.title) existing.title = meta.title;
    next = list;
  } else {
    // Index entry is created on FIRST append only — mashing "New conversation"
    // never creates ghost rows. MAX_THREADS enforced here.
    next = [...list, {
      id: threadId,
      // null (not a placeholder string) so a later user message can still
      // claim the title; the UI falls back at render
      title: meta?.title || null,
      model: meta?.model || null,
      params: meta?.params || null,
      created_at: now,
      updated_at: now,
      message_count: repo.messages.length,
    }];
    if (next.length > MAX_THREADS) {
      next.sort((a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''));
      const evict = next.shift();
      try { localStorage.removeItem(threadKey(clientId, evict.id)); } catch {}
    }
  }
  trySetItem(indexKey(clientId), JSON.stringify(next), clientId, threadId);
  emit();
}

/* ThreadHistoryAdapter for useLocalRuntime: load() replays the stored
   repository on mount; append() fires for user messages and the final
   assistant message of each run (streaming partials never reach it). */
export function createHistoryAdapter(clientId, threadId, getMeta) {
  return {
    async load() {
      try {
        const repo = readThread(clientId, threadId);
        if (!repo?.messages?.length) return { messages: [] };
        return {
          ...repo,
          // createdAt serializes to an ISO string — revive to Date for import
          messages: repo.messages.map(m => (
            m?.message?.createdAt
              ? { ...m, message: { ...m.message, createdAt: new Date(m.message.createdAt) } }
              : m
          )),
        };
      } catch {
        return { messages: [] };
      }
    },
    async append(item) {
      try {
        const id = item?.message?.id;
        if (!id) return;
        const repo = readThread(clientId, threadId) || { headId: null, messages: [] };
        // Upsert by message id — regenerate re-appends the same-parent item
        const idx = repo.messages.findIndex(m => m.message?.id === id);
        if (idx >= 0) repo.messages[idx] = item; else repo.messages.push(item);
        repo.headId = id;

        const meta = { ...(getMeta?.() || {}) };
        if (item.message.role === 'user') {
          const text = (item.message.content || [])
            .filter(p => p.type === 'text')
            .map(p => p.text)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
          if (text) meta.title = text.slice(0, 60);
        }
        upsertThreadAndIndex(clientId, threadId, repo, meta);
      } catch (e) {
        console.warn('playground threads: append failed', e);
      }
    },
  };
}
